import { must, sb } from "../lib/supabase";
import { Badge, Card, ErrorBox, Loading, useLoad } from "../lib/ui";
import type { Payer } from "../lib/types";

const FIELDS: Array<[string, string]> = [
  ["credentialing_entity", "Who credentials"], ["how_to_apply", "How to apply"], ["caqh_used", "CAQH"],
  ["license_requirements", "License"], ["certification_requirements", "Certification"], ["required_documents", "Documents"],
  ["individual_vs_group", "Individual vs group"], ["state_enrollment_prerequisite", "State enrollment prerequisite"],
  ["attestation", "Attestation"], ["turnaround", "Turnaround"], ["effective_date_backdating", "Effective date / backdating"],
  ["recredentialing_cycle", "Recredentialing"], ["provider_relations_contact", "Provider relations"], ["network_status", "Network status"],
];

export function PayersPage({ slug }: { slug?: string }) {
  const { data, error } = useLoad(async () =>
    must(await sb().from("payers").select("*").eq("active", true).order("priority_tier").order("name")) as Payer[], []);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const p = slug ? data.find((x) => x.slug === slug) : undefined;
  if (p) return <PayerDetail payer={p} />;
  return (
    <Card title={`Payers (${data.length})`}>
      <p class="muted small">From the Phase 0 research (version {(data[0] as any)?.research_version ?? "?"}). Every fact has a source and confidence level. “unknown” means it could not be verified — confirm with the payer.</p>
      <table>
        <thead><tr><th>Tier</th><th>Payer</th><th>States</th><th>Type</th><th>Who credentials</th><th>Network</th></tr></thead>
        <tbody>{data.map((py) => (
          <tr>
            <td>T{py.priority_tier}</td><td><a href={`#/payers/${py.slug}`}>{py.name}</a></td><td>{py.states.join(", ")}</td>
            <td class="small">{py.payer_type.replaceAll("_", " ")}</td>
            <td class="small">{py.credentialing_entity?.split(/[.;—]/)[0] ?? <span class="muted">unknown</span>}</td>
            <td class="small">{py.network_status ? <span class="badge warn">{py.network_status.split(/[.(]/)[0]}</span> : <span class="muted">unknown</span>}</td>
          </tr>))}</tbody>
      </table>
    </Card>
  );
}

function PayerDetail({ payer }: { payer: Payer }) {
  const r = payer.research ?? {};
  const { data: reqs } = useLoad(async () =>
    must(await sb().from("payer_requirements").select("*").eq("payer_id", payer.id).order("requirement_key")) as any[], [payer.id]);
  return (
    <>
      <p><a href="#/payers">← All payers</a></p>
      <Card title={payer.name}>
        <p class="muted small">{payer.states.join(", ")} · {payer.payer_type.replaceAll("_", " ")} · Tier {payer.priority_tier}</p>
        <dl class="facts wide">{FIELDS.map(([k, label]) => {
          const f = r[k];
          return <><dt>{label}</dt><dd>{!f || f.value === "unknown" ? <span class="muted">unknown{f?.note ? ` — ${f.note}` : ""}</span> :
            <>{f.value} <Badge value={f.confidence} /></>}</dd></>;
        })}</dl>
        {r.notes?.length > 0 && <><h3>Notes</h3><ul>{r.notes.map((n: any) => <li>{n.value} <Badge value={n.confidence} /></li>)}</ul></>}
        {r.open_questions?.length > 0 && <><h3>Open questions to verify with this payer</h3><ul>{r.open_questions.map((q: string) => <li>{q}</li>)}</ul></>}
      </Card>
      <Card title="Checklist requirements (generated from the research)">
        {!reqs ? <Loading /> : (
          <table>
            <thead><tr><th>Requirement</th><th>State</th><th>Required?</th><th>Confidence</th><th>Sources</th></tr></thead>
            <tbody>{reqs.map((q) => (
              <tr><td>{q.label}{q.note && <div class="muted small">{q.note}</div>}</td><td>{q.state ?? "any"}</td>
                <td>{q.required === "unknown" ? <span class="badge warn">verify</span> : q.required}</td><td><Badge value={q.confidence} /></td>
                <td class="small">{q.source_urls.map((u: string, i: number) => <a href={u} target="_blank" rel="noopener noreferrer">[{i + 1}] </a>)}</td></tr>))}</tbody>
          </table>
        )}
      </Card>
    </>
  );
}
